import { Box, CircularProgress, Switch } from '@mui/material';
import { useQuery } from '@parkchirp/api-client';
import { HourlyFunctionGroupsEnum, PermissionDomainEnum } from 'parkchirp-shared/enums';
import { useCallback, useMemo } from 'react';
import { LIST_PERMISSION_FUNCTION_GROUPS_V2 } from '../../queries';
import { PermissionFunctionGroup } from '../../types';

export interface PortalFeaturesEditorProps {
	functionGroups?: PermissionFunctionGroup[];
	parentFunctionGroups?: PermissionFunctionGroup[] | null;
	canEdit: boolean;
	canAdd?: boolean;
	onChange: (functionGroups: PermissionFunctionGroup[]) => void;
}

export const PortalFeaturesEditor = ({
	functionGroups,
	parentFunctionGroups,
	canEdit,
	canAdd = true,
	onChange,
}: PortalFeaturesEditorProps) => {
	const { data: functionGroupsData, loading: functionGroupsLoading } = useQuery(LIST_PERMISSION_FUNCTION_GROUPS_V2, {
		variables: {
			limit: 0,
			offset: 0,
			filters: [{ propertyName: 'domain', op: 'in', value: [PermissionDomainEnum.Hourly] }],
			order: { field: 'name', sort: 'asc' },
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		skip: !canAdd || !!parentFunctionGroups,
		fetchPolicy: 'network-only',
		ssr: false,
	});

	const featureSet = useMemo(() => {
		if (!parentFunctionGroups && !functionGroupsLoading && functionGroupsData) {
			return functionGroupsData.listPermissionFunctionGroups.functionGroups;
		}

		return parentFunctionGroups?.map((functionGroup) => functionGroup.functionGroup) ?? [];
	}, [parentFunctionGroups, functionGroupsData, functionGroupsLoading]);

	const featureState = useMemo(
		() => functionGroups?.map((feature) => feature.functionGroup.id) ?? [],
		[functionGroups],
	);

	const handleFeatureSwitch = useCallback(
		(event, featureId) => {
			onChange(
				featureSet
					.filter((feature) =>
						feature.id === featureId ? event.target.checked : featureState.includes(feature.id),
					)
					.map((functionGroup) => ({ functionGroup })),
			);
		},
		[featureSet, featureState, onChange],
	);

	return (
		<Box sx={{ mt: 3 }}>
			<h4 style={{ marginBottom: '10px' }}>Features</h4>
			{functionGroupsLoading && <CircularProgress color='inherit' />}
			{!functionGroupsLoading &&
				featureSet.map(
					(feature) =>
						feature.name !== HourlyFunctionGroupsEnum.BASIC_ACCESS && (
							<div key={feature.id}>
								{feature.description}
								<Switch
									disabled={!canEdit}
									checked={featureState.includes(feature.id)}
									onChange={(event) => handleFeatureSwitch(event, feature.id)}
								/>
							</div>
						),
				)}
		</Box>
	);
};
